import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getVenueProductCategories } from '../../firebase/productFirestore';
import { loadVenue } from './venue.slice';
import { accountCleared } from '../actions';
import type { AppDispatch, RootState } from '../store';
import type { LoadingState } from './session.slice';

export interface AdminVenue {
  id: string;
  venueId: string;
  userId: string;
  role: Pagcomanda.Role[];
}

interface AdminState {
  venuesStatus: LoadingState;
  venues: AdminVenue[];
  activeVenueId: string | null;
  error: string | null;
  categories: Array<{ id: string; data: Pagcomanda.ProductCategory }>;
  categoriesStatus: LoadingState;
  categoriesRequestId: string | null;
  categoriesError: string | null;
}

const initialState: AdminState = {
  venuesStatus: 'idle',
  venues: [],
  activeVenueId: null,
  error: null,
  categories: [],
  categoriesStatus: 'idle',
  categoriesRequestId: null,
  categoriesError: null,
};

export const loadAdminCategories = createAsyncThunk(
  'admin/loadCategories',
  async (venueId: string) => getVenueProductCategories(venueId, { publishedOnly: false })
    .then((docs) => docs.map((doc) => ({ id: doc.id, data: doc.data() }))),
);

const sortCategories = (
  categories: Array<{ id: string; data: Pagcomanda.ProductCategory }>,
) => [...categories].sort((a, b) => {
  const orderA = typeof a.data?.order === 'number' ? a.data.order : 999999;
  const orderB = typeof b.data?.order === 'number' ? b.data.order : 999999;
  if (orderA !== orderB) {
    return orderA - orderB;
  }
  return (a.data?.description || '').localeCompare(b.data?.description || '');
});

export const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    venuesLoading: (state) => ({
      ...state,
      venuesStatus: 'pending',
      venues: [],
      error: null,
    }),
    venuesReceived: (state, action: PayloadAction<AdminVenue[]>) => ({
      ...state,
      venuesStatus: 'succeeded',
      venues: action.payload,
      activeVenueId: action.payload.some((venue) => venue.venueId === state.activeVenueId)
        ? state.activeVenueId
        : null,
      error: null,
    }),
    venuesFailed: (state, action: PayloadAction<string>) => ({
      ...state,
      venuesStatus: 'failed',
      error: action.payload,
    }),
    activeVenueChanged: (state, action: PayloadAction<string>) => ({
      ...state,
      activeVenueId: action.payload,
      categories: [],
      categoriesStatus: 'idle',
      categoriesRequestId: null,
      categoriesError: null,
    }),
    activeVenueCleared: (state) => ({
      ...state,
      activeVenueId: null,
      categories: [],
      categoriesStatus: 'idle',
      categoriesRequestId: null,
      categoriesError: null,
    }),
    categoriesReordered: (state, action: PayloadAction<string[]>) => {
      const byId = new Map(state.categories.map((category) => [category.id, category]));
      const seen = new Set<string>();
      const ordered = action.payload
        .map((id) => {
          const category = byId.get(id);
          if (category) seen.add(id);
          return category;
        })
        .filter((category): category is { id: string; data: Pagcomanda.ProductCategory } => (
          category !== undefined
        ));
      const rest = state.categories.filter((category) => !seen.has(category.id));
      const categories = [...ordered, ...rest].map((category, index) => ({
        ...category,
        data: { ...category.data, order: index },
      }));
      return { ...state, categories };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(accountCleared, () => initialState);
    builder.addCase(loadAdminCategories.pending, (state, action) => {
      if (action.meta.arg !== state.activeVenueId) return state;
      return {
        ...state,
        categoriesStatus: 'pending',
        categoriesRequestId: action.meta.requestId,
        categoriesError: null,
      };
    });
    builder.addCase(loadAdminCategories.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.categoriesRequestId) return state;
      return {
        ...state,
        categories: sortCategories(action.payload),
        categoriesStatus: 'succeeded',
      };
    });
    builder.addCase(loadAdminCategories.rejected, (state, action) => {
      if (action.meta.requestId !== state.categoriesRequestId) return state;
      return {
        ...state,
        categoriesStatus: 'failed',
        categoriesError: action.error.code ?? action.error.message ?? 'unknown',
      };
    });
  },
});

export const {
  venuesLoading,
  venuesReceived,
  venuesFailed,
  activeVenueChanged,
  activeVenueCleared,
  categoriesReordered,
} = adminSlice.actions;

export const activateAdminVenue = (venueId: string) => (
  dispatch: AppDispatch,
  getState: () => RootState,
) => {
  const state = getState();
  const canManageVenue = state.admin.venuesStatus === 'succeeded'
    && state.admin.venues.some((venue) => venue.venueId === venueId);
  if (!canManageVenue) return;

  const isAlreadyLoading = state.admin.activeVenueId === venueId
    && ['pending', 'succeeded'].includes(state.venue.loadingVenue);
  if (isAlreadyLoading) return;

  dispatch(activeVenueChanged(venueId));
  dispatch(loadVenue(venueId));
  dispatch(loadAdminCategories(venueId));
};

export const selectAdmin = (state: RootState) => state.admin;
export const selectAdminVenues = (state: RootState) => state.admin.venues;
export const selectAdminVenuesStatus = (state: RootState) => state.admin.venuesStatus;
export const selectActiveVenueId = (state: RootState) => state.admin.activeVenueId;
export const selectAdminCategories = (state: RootState) => state.admin.categories;
export const selectAdminCategoriesStatus = (state: RootState) => state.admin.categoriesStatus;
export const selectAdminCategoriesError = (state: RootState) => state.admin.categoriesError;
export const selectActiveVenueData = (state: RootState) => (
  state.admin.activeVenueId === state.venue.venueId ? state.venue.venueData : null
);

export default adminSlice.reducer;
